import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAtom } from 'jotai'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { trpc, trpcClient } from '@/lib/trpc'
import type { Comment } from '@/tiptap/tiptap-extensions/custom/comments/types'
import type { Editor } from '@tiptap/react'
import { workspaceIdAtom } from '@/stores/workspaceStore'

type Resolution = 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED'

interface UseCommentsSyncControllerOptions {
  docId?: string
  branchId?: string
  policySubmissionId?: string
}

interface UseCommentsSyncControllerResult {
  comments: Comment[]
  attachEditor: (editor: Editor | null) => void
  mergeFromEditor: (data: Comment[] | Map<string, any>) => Comment[] | void
  refresh: (forceEditorReload?: boolean) => Promise<void>
  resolveComment: (id: string, resolution: Resolution) => Promise<void>
  deleteComment: (id: string) => Promise<void>
  replyComment: (parentId: string, content: string, extra?: { mentions?: string[]; richContent?: any }) => Promise<void>
  addPageComment: (content: string, pageSection?: string, extra?: { mentions?: string[]; richContent?: any }) => Promise<void>
  addBlockComment: (blockId: string, content: string, extra?: { mentions?: string[]; richContent?: any }) => Promise<void>
}

function normalizeArray(input: Comment[] | Map<string, any> | null | undefined): Comment[] {
  if (!input) return []
  if (Array.isArray(input)) return input as Comment[]
  if (input instanceof Map) return Array.from(input.values()) as Comment[]
  return []
}

function computeKey(c: Comment): string {
  // Prefer stable id for dedup to avoid treating edits as new items
  if (c.id) return c.id
  const text = ((c.text ?? c.content ?? '') as string).trim()
  const range = c.textRange ? `${c.textRange.start}-${c.textRange.end}` : ''
  const block = c.docBlockId || ''
  const parent = c.parentId || ''
  return `${c.scope}:${parent}:${block}:${range}:${text}`
}

function preferNewerOrNonTemp(a: Comment | undefined, b: Comment): Comment {
  if (!a) return b
  const aTemp = a.id?.includes('temp')
  const bTemp = b.id?.includes('temp')
  if (aTemp && !bTemp) return b
  if (!aTemp && bTemp) return a
  const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
  const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
  return bTime >= aTime ? b : a
}

function mergeById(existing: Comment[], incoming: Comment[]): Comment[] {
  const map = new Map<string, Comment>()
  for (const c of existing) {
    map.set(computeKey(c), c)
  }
  for (const c of incoming) {
    const key = computeKey(c)
    map.set(key, preferNewerOrNonTemp(map.get(key), c))
  }
  return Array.from(map.values())
}

function flattenComments(comments: Comment[]): Comment[] {
  const flat: Comment[] = []
  for (const c of comments || []) {
    const { replies, ...rest } = c as any
    flat.push(rest as Comment)
    if (Array.isArray(replies) && replies.length > 0) {
      for (const r of replies) {
        // ensure parentId is set when flattening
        const reply: Comment = { ...r, parentId: r.parentId || c.id }
        flat.push(reply)
      }
    }
  }
  return flat
}

function nestReplies(comments: Comment[]): Comment[] {
  const parents = comments.filter((c) => !c.parentId)
  const replies = comments.filter((c) => !!c.parentId)
  const byParent = new Map<string, Comment[]>()
  for (const r of replies) {
    const parentId = r.parentId as string
    const list = byParent.get(parentId) || []
    list.push(r)
    byParent.set(parentId, list)
  }
  return parents.map((p) => {
    const incomingReplies = byParent.get(p.id) || []
    // preserve existing replies if present, then merge by id
    const existingReplies = Array.isArray((p as any).replies)
      ? (((p as any).replies as Comment[]) || [])
      : []
    const merged = mergeById(existingReplies, incomingReplies)
    // sort by createdAt asc if available
    merged.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return ta - tb
    })
    return { ...(p as any), replies: merged } as Comment
  })
}

export function useCommentsSyncController(
  options: UseCommentsSyncControllerOptions
): UseCommentsSyncControllerResult {
  const { docId, branchId, policySubmissionId } = options
  const queryClient = useQueryClient()
  const editorRef = useRef<Editor | null>(null)
  const lastUpdateOriginRef = useRef<'editor' | 'controller' | null>(null)
  const [workspaceId] = useAtom(workspaceIdAtom)

  const queryKey = useMemo(() => {
    if (!docId) return null
    return trpc.comments.getComments.queryKey({
      docId,
      includeReplies: true,
      includeResolved: true,
      branchId,
      policySubmissionId,
    })
  }, [docId, branchId, policySubmissionId])

  const fullQueryOptions = useMemo(() => {
    return docId
      ? trpc.comments.getComments.queryOptions({
          docId,
          includeReplies: true,
          includeResolved: true,
          branchId,
          policySubmissionId,
        })
      : null
  }, [docId, branchId, policySubmissionId])

  const { data: initial } = useQuery(
    fullQueryOptions ? {
      ...fullQueryOptions,
      enabled: !!docId && !!fullQueryOptions,
    } : {
      queryKey: ['comments-disabled'],
      queryFn: () => [],
      enabled: false,
    }
  )

  const comments = useMemo(() => {
    return (initial as any as Comment[]) || []
  }, [initial])

  // When cache changes (via initial/refetch or external set), push to editor unless change came from editor
  useEffect(() => {
    if (!editorRef.current) return
    if (!comments) return
    if (lastUpdateOriginRef.current === 'editor') {
      lastUpdateOriginRef.current = null
      return
    }
    try {
      editorRef.current.commands.updateComments(comments)
    } catch (_e) {
      // noop
    }
  }, [comments])

  const attachEditor = useCallback((editor: Editor | null) => {
    editorRef.current = editor
  }, [])

  const mergeFromEditor = useCallback(
    (data: Comment[] | Map<string, any>) => {
      if (!queryKey) return
      const incoming = normalizeArray(data)
      lastUpdateOriginRef.current = 'editor'
      let merged: Comment[] = []
      queryClient.setQueryData(queryKey, (_old: any) => {
        // Treat incoming as the authoritative snapshot from the editor.
        // Dedupe by structural key, then rebuild nested structure.
        const dedup = new Map<string, Comment>()
        for (const c of incoming) {
          dedup.set(computeKey(c), c)
        }
        const incomingFlat = Array.from(dedup.values())
        merged = nestReplies(incomingFlat)
        return merged
      })
      return merged
    },
    [queryKey, queryClient]
  )

  const refresh = useCallback(async (forceEditorReload?: boolean) => {
    if (!queryKey) return
    await queryClient.invalidateQueries({ queryKey, refetchType: 'active' })
    if (forceEditorReload && editorRef.current) {
      try {
        editorRef.current.commands.reloadComments(true)
      } catch (_e) { /*noop*/ }
    }
  }, [queryKey, queryClient])

  const resolveComment = useCallback(
    async (id: string, resolution: Resolution) => {
      if (!queryKey) return
      // optimistic on flat, then re-nest
      queryClient.setQueryData(queryKey, (old: any) => {
        const flat = flattenComments((old as Comment[]) || [])
        const updated = flat.map((c) =>
          c.id === id ? { ...c, commentStatus: resolution } : c
        )
        return nestReplies(updated)
      })

      // push to editor
      try {
        if (editorRef.current) {
          editorRef.current.commands.commentResolve(id, resolution)
        }
      } catch (_e) { /*noop*/ }

      try {
        await trpcClient.comments.resolveComment.mutate({ id, resolution })
      } finally {
        // Avoid immediate invalidation loop; let background refetch happen naturally if active
        await queryClient.invalidateQueries({ queryKey, refetchType: 'inactive' })
      }
    },
    [queryKey, queryClient]
  )

  const deleteComment = useCallback(
    async (id: string) => {
      if (!queryKey) return
      // optimistic remove (supports both parent and reply deletion)
      queryClient.setQueryData(queryKey, (old: any) => {
        const flat = flattenComments((old as Comment[]) || [])
        const filtered = flat.filter((c) => c.id !== id && c.parentId !== id)
        return nestReplies(filtered)
      })

      try {
        if (editorRef.current) {
          editorRef.current.commands.commentRemove({
            commentId: id,
            skipRemote: true,
          })
        }
      } catch (_e) { /*noop*/ }

      try {
        await trpcClient.comments.deleteComment.mutate({ id, deleteReplies: false })
      } finally {
        await queryClient.invalidateQueries({ queryKey, refetchType: 'inactive' })
      }
    },
    [queryKey, queryClient]
  )

  const replyComment = useCallback(
    async (parentId: string, content: string, extra?: { mentions?: string[]; richContent?: any }) => {
      // prefer editor storage for immediate UI feedback inside editor
      const storage = (editorRef.current as any)?.storage?.comments?.options?.storage
      if (storage) {
        try {
          const reply = await storage.createReply(parentId, content, { mentions: extra?.mentions, richContent: extra?.richContent })
          // Optimistically merge the new reply into cache for instant panel refresh
          if (queryKey) {
            queryClient.setQueryData(queryKey, (old: any) => {
              const existing = flattenComments((old as Comment[]) || [])
              const incoming = reply ? [{ ...(reply as Comment), parentId }] : []
              const mergedFlat = mergeById(existing, incoming)
              return nestReplies(mergedFlat)
            })
          }
          // Fire API as source of truth (dual write)
          try {
            await (trpcClient as any)?.comments?.createReply?.mutate?.({ parentId, content, mentions: extra?.mentions || [], richContent: extra?.richContent })
          } catch (apiErr) {
            console.warn('[useCommentsSyncController] api.createReply failed (non-fatal, will rely on cache/next refetch)', apiErr)
          }
          // Ask editor to reload to reconcile ids/marks
          try {
            editorRef.current?.commands.clearCommentCache()
            editorRef.current?.commands.reloadComments(true)
          } catch (_e) {
            // noop
          }
        } catch (err) {
          console.error('[useCommentsSyncController] storage.createReply failed, fallback to api', err)
          // fallback to api
          try {
            await (trpcClient as any)?.comments?.createReply?.mutate?.({ parentId, content, mentions: extra?.mentions || [], richContent: extra?.richContent })
          } catch (apiErr) {
            console.error('[useCommentsSyncController] api.createReply failed (fallback)', apiErr)
          }
        } finally {
          if (queryKey) await queryClient.invalidateQueries({ queryKey })
        }
        return
      }

      // No editor storage: use api only
      try {
        await (trpcClient as any)?.comments?.createReply?.mutate?.({ parentId, content, richContent: extra?.richContent, mentions: extra?.mentions || [], })
      } catch (apiErr) {
        console.error('[useCommentsSyncController] api.createReply failed (no storage)', apiErr)
      }
      if (queryKey) await queryClient.invalidateQueries({ queryKey })
    },
    [queryKey, queryClient]
  )

  const addPageComment = useCallback(
    async (content: string, pageSection?: string, extra?: { mentions?: string[]; richContent?: any }) => {
      // If mentions/rich content provided, create directly via API to capture mentions
      if (extra && (extra.mentions?.length || extra.richContent)) {
        try {
          // Attempt to create via API directly
          await (trpcClient as any)?.comments?.createComment?.mutate?.({
            scope: 'PAGE',
            docId: docId as string,
            workspaceId: workspaceId,
            content,
            pageSection,
            mentions: extra.mentions || [],
            richContent: extra.richContent,
            policySubmissionId,
          })
        } catch (apiErr) {
          console.error('[useCommentsSyncController] api.createComment (with mentions) failed', apiErr)
        } finally {
          await refresh(true)
        }
        return
      }
      if (editorRef.current) {
        try {
          editorRef.current.commands.addPageComment(content, pageSection)
          await refresh(true)
          return
        } catch (_e) {
          // noop, fallback below
        }
      }
      // fallback to server if editor not available
      await (trpcClient as any)?.comments?.createPageComment?.mutate?.({
        content,
        pageSection,
        docId: docId as string,
        policySubmissionId,
      })
      await refresh(true)
    },
    [refresh, docId, workspaceId, policySubmissionId]
  )

  const addBlockComment = useCallback(
    async (blockId: string, content: string, extra?: { mentions?: string[]; richContent?: any }) => {
      if (editorRef.current) {
        try {
          editorRef.current.commands.addBlockComment(blockId, content, extra)
          await refresh(true)
          return
        } catch (_e) {
          // fall through to API
        }
      }

      try {
        await (trpcClient as any)?.comments?.createComment?.mutate?.({
          scope: 'BLOCK',
          docId: docId as string,
          workspaceId,
          docBlockId: blockId,
          content,
          policySubmissionId,
          ...(extra?.richContent ? { metadata: { content: extra.richContent } } : {}),
          ...(extra?.mentions ? { mentions: extra.mentions } : {}),
        })
      } catch (apiErr) {
        console.error('[useCommentsSyncController] api.createComment (block) failed', apiErr)
      } finally {
        await refresh(true)
      }
    },
    [refresh, docId, workspaceId, policySubmissionId]
  )

  return {
    comments,
    attachEditor,
    mergeFromEditor,
    refresh,
    resolveComment,
    deleteComment,
    replyComment,
    addPageComment,
    addBlockComment,
  }
}
